import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { Select } from '@mantine/core';
import { useEventListener } from 'ahooks';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { NumberInputGroup } from '@shakkerai/pro-components';
import { setTempState } from '@starflow/app/store';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { vaeOptions } from '@shakkerai/pro-constants';
import { SD3_MODELS_FAMILY, MODEL_SDXL, SDXL_MODELS_FAMILY } from '@shakkerai/pro-models';
import { mc } from '@shakkerai/report';

const widthFreeSelectComboxProps = {
  position: 'bottom-end',
  width: 'auto',
};

export const AdvancedParams = React.memo((props) => {
  const { disabled, generator } = props;
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const modelBaseType = generator.data.baseType || MODEL_SDXL;
  const isSDXL = SDXL_MODELS_FAMILY[modelBaseType];
  const isSD3 = SD3_MODELS_FAMILY[modelBaseType];

  const handleChange = useCallback(
    (value, name) => {
      updateUnitData(generator.id, { [name]: value });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [generator.id],
  );

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="advanced-generate-options"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const ref = document.getElementById(`advanced-options-entry-${generator.id}`);
    const { x, y } = ref.getBoundingClientRect();

    const maxY = window.innerHeight;

    setPosition({ x: x - 550, y: Math.min(y + 10, maxY - 10) });

    mc('tool.shake.pamaters');
  }, [generator.id]);

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd default={position} scale={1} className="z-[100]" dragHandleClassName="sub-drag-handle">
      <div ref={containerRef} className="w-[280px] bg-white shadow-light rounded-xs">
        <div className="sub-drag-handle flex items-center justify-between px-4 py-2 cursor-move border-b border-[#efefef] ">
          <span className="text-mini font-semibold text-caption-500">{translate('parameter_title')}</span>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
          </button>
        </div>
        <div className="flex flex-col items-stretch mt-4 px-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-mini text-caption-400">{translate('parameter_vae')}</span>
            <div className="flex w-40 items-center h-4">
              <Select
                size="xxxs"
                data={vaeOptions}
                className={`cn-select bordered flex-1`}
                classNames={{
                  dropdown: 'dark-select-dropdown',
                  input: 'text-right !pr-6',
                }}
                value={isSDXL || isSD3 ? '-1' : generator.data.vae}
                comboboxProps={widthFreeSelectComboxProps}
                onChange={(value) => handleChange(value, 'vae')}
                disabled={isSDXL || isSD3 || disabled}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-mini text-caption-400">{translate('parameter_clip')}</span>
            <div className="flex w-40 items-center h-4">
              <NumberInputGroup
                min={1}
                max={12}
                step={1}
                name="clipSkip"
                inputWidth={32}
                disabled={disabled}
                value={generator.data.clipSkip}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});
